import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebaseConfig";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { checkAccess } from "../accessControl";
import { useDarkMode } from "../DarkModeContext";
import { saveProfileHybrid, loadProfileHybrid, migrateLocalStorageToFirebase } from "../userDataSync";
import { redeemGroupCode } from "../groupCodes/redeemCode";
import Header from "../components/Header";
import TabBar from "../components/TabBar";

// Tour badge data
const TOUR_BADGES = [
  {
    id: "san-juan",
    slug: "san-juan-cofresi-treasure", // Actual tour slug used in localStorage
    accessId: "san-juan", // ID used for Firebase access control
    name: "San Juan",
    location: "Puerto Rico",
    image: "/images/badges/san-juan-badge.png",
    distance: 2.3, // miles
  },
  {
    id: "chicago",
    slug: "chicago", // Actual tour slug used in localStorage
    accessId: "chicago-scavenger-hunt-tour", // ID used for Firebase access control
    name: "Chicago",
    location: "Illinois",
    image: "/images/badges/chicago-badge.png",
    distance: 1.0,
  },
  {
    id: "mexico-city",
    slug: "mexico-city-artifact-protocol", // Actual tour slug used in localStorage
    accessId: "mexico-city-scavenger-hunt-tour", // ID used for Firebase access control
    name: "Mexico City",
    location: "Mexico",
    image: "/images/badges/mexico-city-badge.png",
    distance: 1.5,
  },
];

export default function Profile() {
  const [user, setUser] = useState(null);
  const [purchasedTours, setPurchasedTours] = useState(null);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [avatarColor, setAvatarColor] = useState("blue-green");
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showRedeemCode, setShowRedeemCode] = useState(false);
  const [groupCode, setGroupCode] = useState("");
  const [redeemStatus, setRedeemStatus] = useState({ type: "", message: "" });
  const [isRedeeming, setIsRedeeming] = useState(false);
  const { darkMode, toggleDarkMode } = useDarkMode();
  const navigate = useNavigate();

  // Avatar color options - Brand colors
  const avatarColors = [
    { id: "blue-green", gradient: "from-[#8FB3D3] to-[#385B37]", name: "Ocean" },
    { id: "sunset-orange", gradient: "from-[#F8662B] to-[#F8662B]", name: "Sunset" },
    { id: "earth-tone", gradient: "from-[#E4B4A6] to-[#413C04]", name: "Earth" },
    { id: "forest-green", gradient: "from-[#385B37] to-[#385B37]", name: "Forest" },
    { id: "sky-blue", gradient: "from-[#8FB3D3] to-[#8FB3D3]", name: "Sky" },
    { id: "warm-peach", gradient: "from-[#E4B4A6] to-[#F8662B]", name: "Warmth" },
  ];

  // Load profile preferences (hybrid: Firebase first, fallback to localStorage)
  useEffect(() => {
    const loadProfile = async () => {
      if (user) {
        try {
          // Trigger one-time migration from localStorage to Firebase
          await migrateLocalStorageToFirebase(user.uid, user.email);

          // Load profile using hybrid approach
          const profile = await loadProfileHybrid(user.uid, user.email);

          if (profile) {
            setDisplayName(profile.displayName || "");
            setAvatarColor(profile.avatarColor || "blue-green");
            setSoundEnabled(profile.soundEnabled !== false);
          }
        } catch (err) {
          console.error("Error loading profile:", err);
        }
      }
    };

    loadProfile();
  }, [user]);

  useEffect(() => {
    const fetchAccess = async (currentUser) => {
      if (currentUser) {
        try {
          // Check access for all available tours using the correct Firebase access IDs
          const accessPromises = TOUR_BADGES.map(badge =>
            checkAccess(currentUser.email, badge.accessId)
          );
          const accessResults = await Promise.all(accessPromises);

          // Build list of purchased tours
          const purchased = TOUR_BADGES
            .filter((badge, index) => accessResults[index])
            .map(badge => badge.id);

          console.log('[Profile] Purchased tours:', purchased);
          setPurchasedTours(purchased);
        } catch (err) {
          console.error("Access check failed:", err);
          setPurchasedTours([]);
        }
      } else {
        setPurchasedTours([]);
      }
    };

    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      fetchAccess(currentUser);
    });

    return () => unsubscribeAuth();
  }, []);

  // Save profile preferences (hybrid: localStorage + Firebase)
  const saveProfile = async () => {
    if (user) {
      const profile = {
        displayName,
        avatarColor,
        soundEnabled,
      };

      // Save using hybrid approach (localStorage + Firebase)
      await saveProfileHybrid(user.uid, user.email, profile);

      setShowEditProfile(false);

      // Dispatch event to notify other components
      window.dispatchEvent(new Event('profileUpdated'));
    }
  };

  // Handle group code redemption
  const handleRedeemCode = async () => {
    if (!groupCode.trim()) {
      setRedeemStatus({ type: "error", message: "Please enter a code" });
      return;
    }

    setIsRedeeming(true);
    setRedeemStatus({ type: "", message: "" });

    try {
      const result = await redeemGroupCode(user.uid, user.email, groupCode);

      if (result.success) {
        setRedeemStatus({
          type: "success",
          message: `Success! ${result.tourName || "Tour"} is now available`
        });
        setGroupCode("");

        // Clear the access cache for this specific tour so it shows up immediately
        const cacheKey = `access:${user.uid}:${result.tourId}`;
        localStorage.removeItem(cacheKey);
        console.log(`[Profile] Cleared access cache: ${cacheKey}`);

        // Notify other components (like LandingPage) to refresh tours
        window.dispatchEvent(new Event('tours-updated'));
        console.log('[Profile] Dispatched tours-updated event');

        // Refresh purchased tours
        setTimeout(async () => {
          const allowed = await checkAccess(user.email, "san-juan");
          const mexicoAllowed = await checkAccess(user.email, "mexico-city-scavenger-hunt-tour");
          const chicagoAllowed = await checkAccess(user.email, "chicago-scavenger-hunt-tour");
          const tours = [];
          if (allowed) tours.push("san-juan");
          if (mexicoAllowed) tours.push("mexico-city");
          if (chicagoAllowed) tours.push("chicago");
          setPurchasedTours(tours);

          // Close modal after 2 seconds
          setTimeout(() => {
            setShowRedeemCode(false);
            setRedeemStatus({ type: "", message: "" });
          }, 2000);
        }, 500);
      } else {
        setRedeemStatus({
          type: "error",
          message: result.error || "Failed to redeem code"
        });
      }
    } catch (error) {
      console.error("Error redeeming code:", error);
      setRedeemStatus({
        type: "error",
        message: "An error occurred. Please try again."
      });
    } finally {
      setIsRedeeming(false);
    }
  };

  // Calculate stats
  const calculateStats = () => {
    if (!purchasedTours) return { completed: 0, distance: 0, locations: 0, streak: 0 };

    let completed = 0;
    let totalDistance = 0;
    let totalLocations = 0;
    let currentStreak = 0;

    purchasedTours.forEach((tourId) => {
      // Find the badge to get the actual slug
      const tourBadge = TOUR_BADGES.find(b => b.id === tourId);
      if (!tourBadge) return;

      // Use the correct localStorage key format: tib_progress_{slug}
      const progressKey = `tib_progress_${tourBadge.slug}`;
      const progressData = localStorage.getItem(progressKey);

      if (progressData) {
        try {
          const progress = JSON.parse(progressData);
          if (progress.finished) {
            completed++;
            totalDistance += tourBadge.distance;
          }
          // Count answered locations
          totalLocations += Object.keys(progress.answers || {}).length;
          // Get streak
          if (progress.streak) currentStreak = Math.max(currentStreak, progress.streak);
        } catch (err) {
          console.error("Error parsing progress:", err);
        }
      }
    });

    return {
      completed,
      distance: totalDistance,
      locations: totalLocations,
      streak: currentStreak,
    };
  };

  const stats = calculateStats();

  // Check if badge is unlocked
  const isBadgeUnlocked = (tourId) => {
    if (!purchasedTours || !purchasedTours.includes(tourId)) return false;

    // Find the badge to get the actual slug
    const tourBadge = TOUR_BADGES.find(b => b.id === tourId);
    if (!tourBadge) return false;

    // Use the correct localStorage key format: tib_progress_{slug}
    const progressKey = `tib_progress_${tourBadge.slug}`;
    const progressData = localStorage.getItem(progressKey);

    if (!progressData) return false;
    try {
      const progress = JSON.parse(progressData);
      return progress.finished || false;
    } catch {
      return false;
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (err) {
      console.error("Error signing out:", err);
    }
  };

  // Loading state
  if (user && purchasedTours === null) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-500 bg-gray-50">
        Loading profile...
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return (
      <div className="relative min-h-screen text-gray-800 overflow-x-hidden">
        {/* Background */}
        <div className="fixed inset-0 -z-10" style={{ background: 'linear-gradient(to bottom, #FFFADF 0%, rgba(255, 250, 223, 0.8) 50%, #FFFADF 100%)' }} />
        <Header />
        <main
          className="relative z-10 max-w-2xl mx-auto px-6 pb-32 text-center"
          style={{
            paddingTop: 'max(5rem, calc(env(safe-area-inset-top, 0px) + 5rem))'
          }}
        >
          <div className="mt-20">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">👤</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Rubik, sans-serif' }}>
              Sign in to view your profile
            </h1>
            <p className="text-gray-600 mb-8" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              Create an account or log in to track your adventures and earn badges
            </p>
            <button
              onClick={() => navigate("/login")}
              className="px-8 py-4 rounded-full text-white font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 hover:opacity-90"
              style={{ backgroundColor: '#F8662B' }}
            >
              Log In
            </button>
          </div>
        </main>
        <TabBar isAuthed={false} />
      </div>
    );
  }

  return (
    <div className={`relative min-h-screen overflow-x-hidden ${
      darkMode ? "text-gray-100" : "text-gray-800"
    }`}>
      {/* Background */}
      <div className={`fixed inset-0 -z-10 ${
        darkMode
          ? "bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900"
          : ""
      }`} style={{
        background: darkMode
          ? undefined
          : 'linear-gradient(to bottom, #FFFADF 0%, rgba(255, 250, 223, 0.8) 50%, #FFFADF 100%)'
      }} />
      <div className="fixed inset-0 -z-10" style={{ background: 'radial-gradient(circle at 50% 20%, rgba(143, 179, 211, 0.12), transparent 60%)' }} />
      <div
        className="fixed inset-0 -z-10 opacity-[.04] pointer-events-none"
        style={{ backgroundImage: "url(/images/noise-512.png)" }}
      />

      <Header />

      <main
        className="relative z-10 max-w-3xl mx-auto px-6 pb-32"
        style={{
          paddingTop: 'max(5rem, calc(env(safe-area-inset-top, 0px) + 5rem))'
        }}
      >
        {/* User Info Section */}
        <div className="text-center mb-8 relative">
          <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${avatarColors.find(c => c.id === avatarColor)?.gradient || 'from-[#8FB3D3] to-[#385B37]'} flex items-center justify-center text-white text-3xl font-bold mx-auto mb-4 shadow-lg`}>
            {(displayName || user.email)?.[0]?.toUpperCase()}
          </div>
          <h1 className={`text-3xl font-bold mb-1 ${
            darkMode ? "text-gray-100" : "text-gray-900"
          }`} style={{ fontFamily: 'Rubik, sans-serif' }}>
            {displayName || user.email?.split('@')[0]}
          </h1>
          <p className={`text-sm mb-3 ${
            darkMode ? "text-gray-300" : "text-gray-600"
          }`} style={{ fontFamily: 'Urbanist, sans-serif' }}>
            {user.email}
          </p>
          <button
            onClick={() => setShowEditProfile(true)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              darkMode
                ? "bg-gray-800 hover:bg-gray-700 text-gray-200"
                : "bg-gray-100 hover:bg-gray-200 text-gray-700"
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
            Edit Profile
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          <div
            className={`px-4 py-3 rounded-xl transition-all ${
              darkMode ? 'bg-gray-800/50' : 'bg-white'
            }`}
            style={{
              border: darkMode ? '1px solid rgb(55, 65, 81)' : '1px solid #E4B4A6'
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl">🗺️</span>
              <span className={`text-2xl font-bold ${
                darkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                {purchasedTours?.length || 0}
              </span>
            </div>
            <div className={`text-[10px] font-semibold uppercase tracking-wide ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Adventures
            </div>
          </div>

          <div
            className={`px-4 py-3 rounded-xl transition-all ${
              darkMode ? 'bg-gray-800/50' : 'bg-white'
            }`}
            style={{
              border: darkMode ? '1px solid rgb(55, 65, 81)' : '1px solid #E4B4A6'
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl">🏆</span>
              <span className={`text-2xl font-bold ${
                darkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                {stats.completed}
              </span>
            </div>
            <div className={`text-[10px] font-semibold uppercase tracking-wide ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Completed
            </div>
          </div>

          <div
            className={`px-4 py-3 rounded-xl transition-all ${
              darkMode ? 'bg-gray-800/50' : 'bg-white'
            }`}
            style={{
              border: darkMode ? '1px solid rgb(55, 65, 81)' : '1px solid #E4B4A6'
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl">🚶</span>
              <span className={`text-2xl font-bold ${
                darkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                {stats.distance.toFixed(1)}
              </span>
            </div>
            <div className={`text-[10px] font-semibold uppercase tracking-wide ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Miles Explored
            </div>
          </div>

          <div
            className={`px-4 py-3 rounded-xl transition-all ${
              darkMode ? 'bg-gray-800/50' : 'bg-white'
            }`}
            style={{
              border: darkMode ? '1px solid rgb(55, 65, 81)' : '1px solid #E4B4A6'
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl">📍</span>
              <span className={`text-2xl font-bold ${
                darkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                {stats.locations}
              </span>
            </div>
            <div className={`text-[10px] font-semibold uppercase tracking-wide ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Locations Found
            </div>
          </div>
        </div>

        {/* Badges Section */}
        <div className="mb-8">
          <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${
            darkMode ? "text-gray-100" : "text-gray-900"
          }`} style={{ fontFamily: 'Rubik, sans-serif' }}>
            <span>🎖️</span>
            <span>Badges</span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {TOUR_BADGES.map((badge) => {
              const unlocked = isBadgeUnlocked(badge.id);

              return (
                <div
                  key={badge.id}
                  className="flex flex-col items-center text-center"
                >
                  <div
                    className={`relative w-24 h-24 rounded-full overflow-hidden mb-2 transition-all ${
                      unlocked
                        ? "shadow-lg ring-offset-2"
                        : "opacity-40 grayscale"
                    }`}
                    style={unlocked ? { boxShadow: '0 0 0 4px #385B37' } : {}}
                  >
                    {/* Placeholder - replace with actual badge image */}
                    <div className="w-full h-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-4xl">
                      {unlocked ? "🏅" : "🔒"}
                    </div>
                    {/* Uncomment when you add badge images:
                    <img
                      src={badge.image}
                      alt={`${badge.name} badge`}
                      className="w-full h-full object-cover"
                    />
                    */}
                  </div>
                  <h3 className={`text-sm font-bold ${
                    unlocked
                      ? darkMode ? "text-gray-100" : "text-gray-900"
                      : darkMode ? "text-gray-500" : "text-gray-500"
                  }`}>
                    {badge.name}
                  </h3>
                  <p className={`text-xs ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}>
                    {badge.location}
                  </p>
                  {badge.comingSoon && (
                    <span className={`text-[10px] mt-1 ${
                      darkMode ? "text-gray-500" : "text-gray-400"
                    }`}>Coming Soon</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={() => setShowRedeemCode(true)}
            className={`w-full px-6 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
              darkMode
                ? "bg-emerald-900/30 border-2 border-emerald-700 text-emerald-300 hover:bg-emerald-900/50"
                : "bg-emerald-50 border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300"
            }`}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            Redeem Code
          </button>
          <button
            onClick={() => navigate("/terms")}
            className={`w-full px-6 py-3 rounded-xl font-semibold transition-all ${
              darkMode
                ? "bg-gray-800 border-2 border-gray-700 text-gray-200 hover:bg-gray-700"
                : "bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300"
            }`}
          >
            Terms of Use
          </button>
          <button
            onClick={handleSignOut}
            className="w-full px-6 py-3 rounded-xl bg-white border-2 border-red-200 text-red-600 font-semibold hover:bg-red-50 hover:border-red-300 transition-all"
          >
            Sign Out
          </button>
        </div>

        {/* Edit Profile Modal */}
        {showEditProfile && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
            <div className={`rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className={`text-2xl font-bold ${
                  darkMode ? "text-gray-100" : "text-gray-900"
                }`} style={{ fontFamily: 'Rubik, sans-serif' }}>
                  Edit Profile
                </h2>
                <button
                  onClick={() => setShowEditProfile(false)}
                  className={darkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Display Name */}
              <div className="mb-6">
                <label className={`block text-sm font-semibold mb-2 ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}>
                  Display Name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={user.email?.split('@')[0]}
                  className={`w-full px-4 py-3 rounded-xl border outline-none transition-all ${
                    darkMode
                      ? "bg-gray-700 text-gray-100"
                      : "bg-white text-gray-900"
                  }`}
                  style={{
                    borderColor: darkMode ? '#8FB3D3' : '#E4B4A6',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#8FB3D3';
                    e.target.style.boxShadow = darkMode
                      ? '0 0 0 3px rgba(143, 179, 211, 0.3)'
                      : '0 0 0 3px rgba(143, 179, 211, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = darkMode ? '#8FB3D3' : '#E4B4A6';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <p className={`text-xs mt-1 ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}>
                  This name will appear on your profile
                </p>
              </div>

              {/* Avatar Color */}
              <div className="mb-6">
                <label className={`block text-sm font-semibold mb-3 ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}>
                  Avatar Theme
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {avatarColors.map((color) => (
                    <button
                      key={color.id}
                      onClick={() => setAvatarColor(color.id)}
                      className="relative p-3 rounded-xl border-2 transition-all"
                      style={{
                        borderColor: avatarColor === color.id ? '#F8662B' : '#E4B4A6',
                        backgroundColor: avatarColor === color.id
                          ? 'rgba(248, 102, 43, 0.1)'
                          : 'transparent'
                      }}
                    >
                      <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${color.gradient} mx-auto mb-2 flex items-center justify-center text-white font-bold text-lg`}>
                        {(displayName || user.email)?.[0]?.toUpperCase()}
                      </div>
                      <p className="text-xs text-gray-600 text-center font-medium">
                        {color.name}
                      </p>
                      {avatarColor === color.id && (
                        <div
                          className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: '#F8662B' }}
                        >
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Preferences */}
              <div className="mb-6">
                <label className={`block text-sm font-semibold mb-3 ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}>
                  Preferences
                </label>
                <div className="space-y-3">
                  <button
                    onClick={() => setSoundEnabled(!soundEnabled)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${
                      darkMode
                        ? "bg-gray-700 hover:bg-gray-600"
                        : "bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">🔊</span>
                      <div className="text-left">
                        <div className={`text-sm font-semibold ${
                          darkMode ? "text-gray-100" : "text-gray-900"
                        }`}>Sound Effects</div>
                        <div className={`text-xs ${
                          darkMode ? "text-gray-400" : "text-gray-500"
                        }`}>Play sounds during tours</div>
                      </div>
                    </div>
                    <div
                      className="w-12 h-6 rounded-full transition-all"
                      style={{ backgroundColor: soundEnabled ? '#F8662B' : '#E4B4A6' }}
                    >
                      <div className={`w-5 h-5 rounded-full bg-white mt-0.5 transition-all ${soundEnabled ? 'ml-6' : 'ml-0.5'}`} />
                    </div>
                  </button>

                  <button
                    onClick={toggleDarkMode}
                    className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${
                      darkMode
                        ? "bg-gray-700 hover:bg-gray-600"
                        : "bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{darkMode ? '🌙' : '☀️'}</span>
                      <div className="text-left">
                        <div className={`text-sm font-semibold ${
                          darkMode ? "text-gray-100" : "text-gray-900"
                        }`}>Dark Mode</div>
                        <div className={`text-xs ${
                          darkMode ? "text-gray-400" : "text-gray-500"
                        }`}>Switch to {darkMode ? 'light' : 'dark'} theme</div>
                      </div>
                    </div>
                    <div
                      className="w-12 h-6 rounded-full transition-all"
                      style={{ backgroundColor: darkMode ? '#F8662B' : '#E4B4A6' }}
                    >
                      <div className={`w-5 h-5 rounded-full bg-white mt-0.5 transition-all ${darkMode ? 'ml-6' : 'ml-0.5'}`} />
                    </div>
                  </button>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowEditProfile(false)}
                  className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all ${
                    darkMode
                      ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={saveProfile}
                  className="flex-1 px-6 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all hover:opacity-90"
                  style={{ backgroundColor: '#F8662B' }}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Redeem Code Modal */}
        {showRedeemCode && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
            <div className={`rounded-2xl max-w-md w-full p-6 ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className={`text-2xl font-bold ${
                  darkMode ? "text-gray-100" : "text-gray-900"
                }`} style={{ fontFamily: 'Rubik, sans-serif' }}>
                  Redeem Group Code
                </h2>
                <button
                  onClick={() => {
                    setShowRedeemCode(false);
                    setGroupCode("");
                    setRedeemStatus({ type: "", message: "" });
                  }}
                  className={darkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className={`text-sm mb-6 ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}>
                Enter your group access code to unlock a tour
              </p>

              {/* Code Input */}
              <div className="mb-6">
                <label className={`block text-sm font-semibold mb-2 ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}>
                  Access Code
                </label>
                <input
                  type="text"
                  value={groupCode}
                  onChange={(e) => setGroupCode(e.target.value.toUpperCase())}
                  placeholder="Enter your access code"
                  className={`w-full px-4 py-3 rounded-xl border outline-none transition-all font-mono text-lg tracking-wider text-center ${
                    darkMode
                      ? "bg-gray-700 text-gray-100"
                      : "bg-white text-gray-900"
                  }`}
                  style={{
                    borderColor: darkMode ? '#8FB3D3' : '#E4B4A6',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#8FB3D3';
                    e.target.style.boxShadow = darkMode
                      ? '0 0 0 3px rgba(143, 179, 211, 0.3)'
                      : '0 0 0 3px rgba(143, 179, 211, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = darkMode ? '#8FB3D3' : '#E4B4A6';
                    e.target.style.boxShadow = 'none';
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !isRedeeming) {
                      handleRedeemCode();
                    }
                  }}
                  disabled={isRedeeming}
                />
              </div>

              {/* Status Message */}
              {redeemStatus.message && (
                <div className={`mb-4 p-3 rounded-lg ${
                  redeemStatus.type === "success"
                    ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
                    : "bg-red-50 border border-red-200 text-red-700"
                }`}>
                  <div className="flex items-center gap-2">
                    {redeemStatus.type === "success" ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                    <span className="text-sm font-medium">{redeemStatus.message}</span>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRedeemCode(false);
                    setGroupCode("");
                    setRedeemStatus({ type: "", message: "" });
                  }}
                  className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all ${
                    darkMode
                      ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  disabled={isRedeeming}
                >
                  Close
                </button>
                <button
                  onClick={handleRedeemCode}
                  className="flex-1 px-6 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#F8662B' }}
                  disabled={isRedeeming || !groupCode.trim()}
                >
                  {isRedeeming ? "Redeeming..." : "Redeem Code"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      <TabBar isAuthed={!!user} />
    </div>
  );
}
